import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';
import { Plus, Code2, Calendar, Sparkles, LogOut, Trash2, CheckSquare, Square } from 'lucide-react';
import { isDevAuthEnabled } from '../utils/devAuth';
import GitHubPushDialog from '../components/GitHubPushDialog';
import BrandLogo from '../components/BrandLogo';
import SystemStatus from '../components/SystemStatus';

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      if (isDevAuthEnabled()) {
        setProjects([{
          project_id: 'demo_project',
          user_id: 'dev_user',
          name: 'Demo App',
          description: 'Sample project for preview',
          prompt: 'Demo app preview',
          tech_stack: { frontend: 'React', backend: 'FastAPI', database: 'MongoDB' },
          files: [
            { path: 'src/App.js', content: 'export default function App(){return <div style={{padding:20}}><h1>Demo App</h1><p>Hello from Digital Ninja.</p></div>}', language: 'js' },
            { path: 'src/index.css', content: 'body{font-family:sans-serif}', language: 'css' },
          ],
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }]);
      } else {
        toast.error('Failed to load projects');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1c1c1e]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff4500]"></div>
          <p className="text-white mt-4 text-lg">Loading your projects...</p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDelete = async (e, project_id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project? This cannot be undone.')) return;
    try {
      await api.delete(`/projects/${project_id}`);
      setProjects((prev) => prev.filter((p) => p.project_id !== project_id));
      setSelectedProjects((prev) => {
        const next = new Set(prev);
        next.delete(project_id);
        return next;
      });
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const toggleProjectSelection = (e, project_id) => {
    e.stopPropagation();
    setSelectedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(project_id)) {
        next.delete(project_id);
      } else {
        next.add(project_id);
      }
      return next;
    });
  };

  const selectAllProjects = () => {
    if (selectedProjects.size === projects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(projects.map((p) => p.project_id)));
    }
  };

  const deleteSelectedProjects = async () => {
    if (selectedProjects.size === 0) return;
    const count = selectedProjects.size;
    if (!window.confirm(`Are you sure you want to delete ${count} project${count > 1 ? 's' : ''}? This cannot be undone.`)) return;

    let deleted = 0;
    let failed = 0;
    for (const project_id of selectedProjects) {
      try {
        await api.delete(`/projects/${project_id}`);
        deleted++;
      } catch (error) {
        failed++;
      }
    }

    setProjects((prev) => prev.filter((p) => !selectedProjects.has(p.project_id)));
    setSelectedProjects(new Set());
    setIsSelectionMode(false);

    if (deleted > 0) toast.success(`Deleted ${deleted} project${deleted > 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`Failed to delete ${failed} project${failed > 1 ? 's' : ''}`);
  };

  return (
    <div className="h-screen overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6" data-testid="projects-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-10 w-10 rounded-lg shadow-lg" />
            <div>
              <h1 className="text-4xl font-heading font-bold text-white mb-2">Your Projects</h1>
              <p className="text-slate-400">Welcome back, {user.name}</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            {projects.length > 0 && (
              <>
                {isSelectionMode ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={selectAllProjects}
                      className="text-white border-slate-600"
                    >
                      {selectedProjects.size === projects.length ? (
                        <><CheckSquare className="mr-2 h-4 w-4" /> Deselect All</>
                      ) : (
                        <><Square className="mr-2 h-4 w-4" /> Select All ({projects.length})</>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={deleteSelectedProjects}
                      disabled={selectedProjects.size === 0}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Selected ({selectedProjects.size})
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setIsSelectionMode(false); setSelectedProjects(new Set()); }}
                      className="text-white border-slate-600"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setIsSelectionMode(true)}
                    className="text-white border-slate-600"
                  >
                    <CheckSquare className="mr-2 h-4 w-4" />
                    Select Multiple
                  </Button>
                )}
              </>
            )}
            <GitHubPushDialog
              trigger={<Button variant="outline" className="text-white border-slate-600">Push to GitHub</Button>}
              defaultOwner="Poach420"
              defaultRepo="Digital-Ninja-v1"
              defaultBranch="main"
            />
            <Button onClick={() => navigate('/ai-builder')} variant="outline" className="text-white border-slate-600" data-testid="open-ai-builder">
              Open AI Builder
            </Button>
            <Button onClick={() => navigate('/builder')} className="bg-[#ff4500] hover:bg-[#ff5722]" data-testid="new-project-button">
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
            <Button variant="outline" onClick={handleLogout} className="text-white border-slate-600" data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <SystemStatus />

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center text-white py-12">
            <p>Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <Card className="p-12 text-center bg-slate-800/50 border-slate-700">
            <Sparkles className="h-16 w-16 text-[#ff4500] mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-white mb-2">No Projects Yet</h2>
            <p className="text-slate-400 mb-6">Start building your first AI-generated application</p>
            <Button onClick={() => navigate('/builder')} className="bg-[#ff4500] hover:bg-[#ff5722]">
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Project
            </Button>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 pb-12">
            {projects.map((project) => (
              <Card
                key={project.project_id}
                className={`bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-colors cursor-pointer group relative ${selectedProjects.has(project.project_id) ? 'ring-2 ring-[#ff4500] border-[#ff4500]' : ''
                  }`}
                onClick={(e) => {
                  if (isSelectionMode) {
                    toggleProjectSelection(e, project.project_id);
                  } else {
                    navigate(`/builder/${project.project_id}`);
                  }
                }}
                data-testid={`project-${project.project_id}`}
              >
                {isSelectionMode && (
                  <div
                    className="absolute top-3 left-3 z-10"
                    onClick={(e) => toggleProjectSelection(e, project.project_id)}
                  >
                    {selectedProjects.has(project.project_id) ? (
                      <CheckSquare className="h-6 w-6 text-[#ff4500]" />
                    ) : (
                      <Square className="h-6 w-6 text-slate-400 hover:text-white" />
                    )}
                  </div>
                )}
                <CardHeader className={isSelectionMode ? 'pl-12' : ''}>
                  <div className="flex items-start justify-between mb-2">
                    <Code2 className="h-8 w-8 text-[#ff4500]" />
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs text-slate-300 border-slate-600">
                        {project.tech_stack.frontend}
                      </Badge>
                      {!isSelectionMode && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-500 opacity-70 group-hover:opacity-100"
                          title="Delete project"
                          onClick={(e) => handleDelete(e, project.project_id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-white font-heading">{project.name}</CardTitle>
                  <CardDescription className="text-slate-400">{project.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4 text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(project.created_at).toLocaleDateString()}
                      </span>
                      <span>{project.files.length} files</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                      {project.tech_stack.backend}
                    </Badge>
                    <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                      {project.tech_stack.database}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;